/**
 * User's movie ratings page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useRealtimeRefetch } from '../hooks/useRealtimeRefetch';
import apiService, { RatedMovieResponse, PaginatedResponse } from '../services/api';
import StarRating from '../components/StarRating';
import BulkImportModal from '../components/BulkImportModal';
import styled from 'styled-components';

const RatingsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  padding: 0.6rem 0;
`;

const Select = styled.select`
  padding: 0.45rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-text);
  font-size: 0.9rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const SearchInput = styled.input`
  padding: 0.45rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  width: 220px;
  color: var(--color-text);
  background: var(--color-surface);
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const StatsCard = styled.div`
  background: var(--color-surface);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  margin-bottom: 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
  text-align: center;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: var(--color-accent);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
`;

const RatingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const RatingCard = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  overflow: hidden;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const MoviePoster = styled.div<{ $posterUrl?: string }>`
  height: 200px;
  background: ${props => props.$posterUrl
    ? `url(https://image.tmdb.org/t/p/w300${props.$posterUrl}) center/cover`
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  position: relative;
`;

const UserRatingBadge = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: bold;
`;

const MovieInfo = styled.div`
  padding: 1.5rem;
`;

const MovieTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: var(--color-text);
`;

const MovieDetails = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const RatingSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const RatingDate = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.8rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: background 0.2s, color 0.2s;

  ${props => props.$variant === 'primary' ? `
    background: var(--color-accent);
    color: white;
    &:hover { background: var(--color-accent-hover); }
  ` : props.$variant === 'secondary' ? `
    background: transparent;
    color: var(--color-accent);
    border: 1px solid var(--color-accent);
    padding: 0.45rem 0.75rem;
    font-size: 0.9rem;
    &:hover { background: var(--color-accent-subtle); }
  ` : props.$variant === 'danger' ? `
    background: #e53e3e;
    color: white;
    &:hover { background: #c53030; }
  ` : `
    background: var(--color-bg);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    padding: 0.45rem 0.75rem;
    font-size: 0.9rem;
    &:hover { background: var(--color-border); }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-border);
  background: ${props => props.$active ? 'var(--color-accent)' : 'var(--color-surface)'};
  color: ${props => props.$active ? 'white' : 'var(--color-text)'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.$active ? 'var(--color-accent-hover)' : 'var(--color-surface-subtle)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const NoRatings = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--color-text-secondary);
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
`;

// Edit rating modal
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const EditDialog = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
`;

const EditDialogTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  color: var(--color-text);
  font-size: 1.2rem;
`;

const EditDialogSubtitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
`;

const EditDialogButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

interface EditState {
  tmdb_id: number;
  rating_id: number;
  title: string;
  currentRating: number;
}

const RatingsPage: React.FC = () => {
  const [ratings, setRatings] = useState<RatedMovieResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRatings, setTotalRatings] = useState(0);
  const [sortBy, setSortBy] = useState('date_rated');
  const [order, setOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [editRating, setEditRating] = useState(0);
  const [editLoading, setEditLoading] = useState(false);

  const fetchRatings = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError('');

    try {
      const response: PaginatedResponse<RatedMovieResponse> = await apiService.getRatedMovies(
        currentPage, 200, sortBy, order
      );

      let filteredRatings = response.items;
      if (searchQuery.trim()) {
        filteredRatings = response.items.filter(rating =>
          rating.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setRatings(filteredRatings);
      setTotalPages(response.total_pages);
      setTotalRatings(response.total);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : detail || 'Failed to load ratings');
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, order, searchQuery]);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  useRealtimeRefetch(['rated_movies'], () => fetchRatings(true));

  // When search changes, reset to page 1 (which causes fetchRatings to re-run via useCallback dep change)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleDeleteRating = async (movieId: number, ratingId: number) => {
    if (!window.confirm('Are you sure you want to delete this rating?')) return;

    setDeleteLoading(ratingId);
    try {
      await apiService.deleteRating(movieId);
      setRatings(prev => prev.filter(r => r.rating_id !== ratingId));
      setTotalRatings(prev => prev - 1);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete rating');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleOpenEdit = (rating: RatedMovieResponse) => {
    setEditState({
      tmdb_id: rating.tmdb_id,
      rating_id: rating.rating_id,
      title: rating.title,
      currentRating: rating.user_rating,
    });
    setEditRating(rating.user_rating);
  };

  const handleSaveEdit = async () => {
    if (!editState || editRating === 0) return;
    setEditLoading(true);
    try {
      const updated = await apiService.updateRating(editState.tmdb_id, { user_rating: editRating });
      setRatings(prev => prev.map(r => r.rating_id === editState.rating_id ? updated : r));
      setEditState(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update rating');
    } finally {
      setEditLoading(false);
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  const formatReleaseYear = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).getFullYear();
  };

  const averageRating = ratings.length > 0
    ? (ratings.reduce((sum, r) => sum + r.user_rating, 0) / ratings.length).toFixed(1)
    : '0';

  const ratingDistribution = ratings.reduce((acc, rating) => {
    acc[rating.user_rating] = (acc[rating.user_rating] || 0) + 1;
    return acc;
  }, {} as { [key: number]: number });

  const favoriteRating = Object.entries(ratingDistribution)
    .sort(([, a], [, b]) => b - a)[0]?.[0] || '0';

  return (
    <RatingsContainer>
      <StatsCard>
        <StatsGrid>
          <StatItem>
            <StatNumber>{totalRatings}</StatNumber>
            <StatLabel>Total Rated</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>{averageRating}</StatNumber>
            <StatLabel>Average Rating</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>{favoriteRating}</StatNumber>
            <StatLabel>Most Used Rating</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>{ratings.filter(r => r.user_rating >= 4).length}</StatNumber>
            <StatLabel>Great Movies (4+)</StatLabel>
          </StatItem>
        </StatsGrid>
      </StatsCard>

      <ControlsBar>
        <label htmlFor="sort-by-select">Sort by:</label>
        <Select id="sort-by-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="date_rated">Date Rated</option>
          <option value="user_rating">My Rating</option>
          <option value="title">Title</option>
        </Select>
        <Select value={order} onChange={(e) => setOrder(e.target.value)}>
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </Select>
        <SearchInput
          style={{ marginLeft: 'auto' }}
          type="text"
          placeholder="Search movies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {/* Import from CSV — hidden until implemented
        <Button $variant="secondary" onClick={() => setShowImportModal(true)}>
          Import from CSV
        </Button>
        */}
      </ControlsBar>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading && <LoadingSpinner>Loading ratings...</LoadingSpinner>}

      {!loading && ratings.length === 0 && (
        <NoRatings>
          <h3>No ratings found!</h3>
          <p>
            {searchQuery
              ? `No movies found matching "${searchQuery}"`
              : 'Start rating movies to see them here.'
            }
          </p>
        </NoRatings>
      )}

      {!loading && ratings.length > 0 && (
        <>
          <RatingsGrid>
            {ratings.map((rating) => (
              <RatingCard key={rating.id}>
                <MovieInfo>
                  <MovieTitle>{rating.title}</MovieTitle>
                  <MovieDetails>
                    {formatReleaseYear(rating.release_date)}
                    {rating.runtime && ` | ${rating.runtime}m`}
                  </MovieDetails>

                  <RatingSection>
                    <StarRating
                      rating={rating.user_rating}
                      readonly
                      size="small"
                      showText={false}
                    />
                    <RatingDate>
                      Rated on {formatDate(rating.date_rated)}
                    </RatingDate>
                  </RatingSection>

                  <ActionButtons>
                    <Button onClick={() => handleOpenEdit(rating)}>
                      Edit Rating
                    </Button>
                    <Button
                      $variant="danger"
                      onClick={() => handleDeleteRating(rating.tmdb_id, rating.rating_id)}
                      disabled={deleteLoading === rating.rating_id}
                    >
                      {deleteLoading === rating.rating_id ? 'Deleting...' : 'Delete'}
                    </Button>
                  </ActionButtons>
                </MovieInfo>
              </RatingCard>
            ))}
          </RatingsGrid>

          {!searchQuery && totalPages > 1 && (
            <Pagination>
              <PageButton onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                First
              </PageButton>
              <PageButton onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                Previous
              </PageButton>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                if (page > totalPages) return null;
                return (
                  <PageButton
                    key={page}
                    $active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </PageButton>
                );
              })}

              <PageButton onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                Next
              </PageButton>
              <PageButton onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                Last
              </PageButton>
            </Pagination>
          )}
        </>
      )}

      {showImportModal && (
        <BulkImportModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={fetchRatings}
        />
      )}

      {editState && (
        <Overlay onClick={() => !editLoading && setEditState(null)}>
          <EditDialog onClick={e => e.stopPropagation()}>
            <EditDialogTitle>Edit Rating</EditDialogTitle>
            <EditDialogSubtitle>{editState.title}</EditDialogSubtitle>
            <StarRating
              rating={editRating}
              onRatingChange={setEditRating}
              size="large"
            />
            <EditDialogButtons>
              <Button onClick={() => setEditState(null)} disabled={editLoading}>
                Cancel
              </Button>
              <Button
                $variant="primary"
                onClick={handleSaveEdit}
                disabled={editLoading || editRating === 0}
              >
                {editLoading ? 'Saving...' : 'Save'}
              </Button>
            </EditDialogButtons>
          </EditDialog>
        </Overlay>
      )}
    </RatingsContainer>
  );
};

export default RatingsPage;
